// ===================================================================
// Impact — Analytics
// Charts, stats, and breakdowns for damage assessment data
// ===================================================================

import { useEffect, useMemo } from 'react';
import {
  Title, Text, Paper, SimpleGrid, Group, Badge,
  LoadingOverlay,
} from '../../components/gab/GabComponents';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, LineElement,
  PointElement, ArcElement, Tooltip, Legend, Filler,
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { format, startOfDay, eachDayOfInterval } from 'date-fns';
import { useImpactStore } from '../../store/impactStore';
import { useAuthStore } from '../../store/authStore';
import { primary } from '../../lib/colors';

ChartJS.register(
  CategoryScale, LinearScale, BarElement, LineElement,
  PointElement, ArcElement, Tooltip, Legend, Filler,
);

// ── Color Maps ─────────────────────────────────────────────

const CATEGORY_COLORS: Record<string, string> = {
  affected: '#16a34a',
  minor: '#d97706',
  major: '#ea580c',
  destroyed: '#dc2626',
  inaccessible: '#7c3aed',
};

const SEVERITY_COLORS: Record<string, string> = {
  '1': '#16a34a',
  '2': '#22c55e',
  '3': '#d97706',
  '4': '#ea580c',
  '5': '#dc2626',
};

const TYPE_COLORS: Record<string, string> = {
  individual: '#3F72AF',
  public: '#F6B93B',
};

// ── Damage Type Label Map ────────────────────────────────
const DAMAGE_TYPE_LABELS: Record<string, string> = {
  roof: 'Roof', foundation: 'Foundation', flooding: 'Flooding',
  wind: 'Wind', tree: 'Tree/Debris', fire: 'Fire',
  structural: 'Structural', other: 'Other',
  road: 'Road Damage', bridge: 'Bridge', power: 'Power Lines',
  utility: 'Utility', vehicle: 'Vehicle', erosion: 'Erosion',
  school: 'School', hospital: 'Hospital/Medical',
  government: 'Government Building', community: 'Community Facility',
  'water-system': 'Water/Sewer System', communications: 'Communications',
  park: 'Park/Recreation', debris: 'Debris Field',
};

// ── Component ──────────────────────────────────────────────

export default function ImpactAnalytics() {
  const { assessments: allAssessments, loading: isLoading, loadAssessments } = useImpactStore();
  const { activeIncident } = useAuthStore();

  useEffect(() => { loadAssessments(); }, [loadAssessments]);

  // Filter by active incident
  const assessments = useMemo(() => {
    if (!activeIncident) return allAssessments;
    return allAssessments.filter(a => String(a.incidentId) === String(activeIncident.id));
  }, [allAssessments, activeIncident]);

  // Compute stats locally from filtered assessments
  const stats = useMemo(() => {
    const byCategory: Record<string, number> = {};
    const bySeverity: Record<string, number> = {};
    const byType: Record<string, number> = {};
    const byDamageType: Record<string, number> = {};
    let totalHomes = 0;
    assessments.forEach(a => {
      const homes = a.homeCount || 1;
      totalHomes += homes;
      if (a.damageCategory) byCategory[a.damageCategory] = (byCategory[a.damageCategory] || 0) + homes;
      if (a.severity) bySeverity[a.severity] = (bySeverity[a.severity] || 0) + homes;
      if (a.assessmentType) byType[a.assessmentType] = (byType[a.assessmentType] || 0) + homes;
      if (a.damageTypes?.length) a.damageTypes.forEach(dt => { byDamageType[dt] = (byDamageType[dt] || 0) + homes; });
    });
    return { total: totalHomes, records: assessments.length, byCategory, bySeverity, byType, byDamageType };
  }, [assessments]);

  // Total photos (loaded separately, will be 0 for now)
  const totalPhotos = useMemo(
    () => assessments.reduce((sum, a) => sum + (a.photos?.length || 0), 0),
    [assessments],
  );

  // Timeline data
  const timelineData = useMemo(() => {
    const dated = assessments
      .filter(a => a.datecreated)
      .map(a => ({ ...a, _date: new Date(a.datecreated) }))
      .filter(a => !isNaN(a._date.getTime()));

    if (dated.length === 0) return null;

    const sorted = [...dated].sort((a, b) => a._date.getTime() - b._date.getTime());
    const start = startOfDay(sorted[0]._date);
    const end = startOfDay(sorted[sorted.length - 1]._date);
    const days = eachDayOfInterval({ start, end });

    const counts = days.map(day => {
      const dayStr = format(day, 'yyyy-MM-dd');
      return dated.filter(a => format(a._date, 'yyyy-MM-dd') === dayStr).length;
    });

    // Cumulative
    const cumulative: number[] = [];
    counts.reduce((acc, val) => { cumulative.push(acc + val); return acc + val; }, 0);

    return {
      labels: days.map(d => format(d, 'MMM d')),
      counts,
      cumulative,
    };
  }, [assessments]);

  // County breakdown
  const countyData = useMemo(() => {
    const map: Record<string, number> = {};
    assessments.forEach(a => {
      const county = a.county || 'Unknown';
      map[county] = (map[county] || 0) + 1;
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 20);
  }, [assessments]);

  // Damage type breakdown for horizontal bar
  const damageTypeEntries = useMemo(() => {
    return Object.entries(stats.byDamageType)
      .sort((a, b) => b[1] - a[1]);
  }, [stats.byDamageType]);

  const total = assessments.length;
  const geoCount = assessments.filter(a => a.latitude && a.longitude).length;

  if (total === 0 && !isLoading) {
    return (
      <div>
        <Group gap="xs" mb="md">
          <Title order={3} c="black">Analytics</Title>
        </Group>
        <Paper p="xl" radius="sm" withBorder>
          <Text ta="center" c="dimmed">No assessments to analyze. Create some assessments first.</Text>
        </Paper>
      </div>
    );
  }

  // ── Chart Configs ──────────────────────────────────────

  const categoryChartData = {
    labels: Object.keys(stats.byCategory).map(k => k.charAt(0).toUpperCase() + k.slice(1)),
    datasets: [{
      label: 'Assessments',
      data: Object.values(stats.byCategory),
      backgroundColor: Object.keys(stats.byCategory).map(k => CATEGORY_COLORS[k] || '#6b7280'),
      borderRadius: 4,
    }],
  };

  const typeChartData = {
    labels: Object.keys(stats.byType).map(k => k.charAt(0).toUpperCase() + k.slice(1)),
    datasets: [{
      data: Object.values(stats.byType),
      backgroundColor: Object.keys(stats.byType).map(k => TYPE_COLORS[k] || '#6b7280'),
      borderWidth: 0,
    }],
  };

  const severityChartData = {
    labels: Object.keys(stats.bySeverity).map(k => `Severity ${k}`),
    datasets: [{
      label: 'Assessments',
      data: Object.values(stats.bySeverity),
      backgroundColor: Object.keys(stats.bySeverity).map(k => SEVERITY_COLORS[k] || '#6b7280'),
      borderRadius: 4,
    }],
  };

  const damageTypeChartData = {
    labels: damageTypeEntries.map(([k]) => DAMAGE_TYPE_LABELS[k] || k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())),
    datasets: [{
      label: 'Count',
      data: damageTypeEntries.map(([, v]) => v),
      backgroundColor: primary[700],
      borderRadius: 4,
    }],
  };

  const timelineChartData = timelineData ? {
    labels: timelineData.labels,
    datasets: [
      {
        label: 'Daily',
        data: timelineData.counts,
        borderColor: primary[700],
        backgroundColor: 'rgba(75, 63, 255, 0.1)',
        fill: true,
        tension: 0.3,
        pointRadius: 2,
      },
      {
        label: 'Cumulative',
        data: timelineData.cumulative,
        borderColor: '#d97706',
        backgroundColor: 'transparent',
        borderDash: [4, 4],
        tension: 0.3,
        pointRadius: 0,
        yAxisID: 'y1',
      },
    ],
  } : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      y: { beginAtZero: true, ticks: { stepSize: 1 } },
    },
  };

  const horizontalBarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y' as const,
    plugins: { legend: { display: false } },
    scales: {
      x: { beginAtZero: true, ticks: { stepSize: 1 } },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'bottom' as const, labels: { padding: 12, usePointStyle: true } } },
  };

  const timelineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'top' as const, labels: { usePointStyle: true } } },
    scales: {
      y: { beginAtZero: true, title: { display: true, text: 'Daily' } },
      y1: { position: 'right' as const, beginAtZero: true, title: { display: true, text: 'Cumulative' }, grid: { drawOnChartArea: false } },
    },
  };

  return (
    <div style={{ position: 'relative' }}>
      <LoadingOverlay visible={isLoading && assessments.length === 0} />

      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <Title order={3} c="black">Analytics</Title>
          {activeIncident && (
            <Badge size="sm" variant="light" color="blue">{activeIncident.name}</Badge>
          )}
        </Group>
      </Group>

      {/* Key Metrics Row */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} mb="md" spacing="sm">
        <Paper p="sm" radius="sm" withBorder ta="center" style={{ borderTop: '3px solid var(--color-primary-800)' }}>
          <Text size="xs" c="dimmed">Total Homes/Units</Text>
          <Text fz={28} fw={700}>{stats.total}</Text>
        </Paper>
        <Paper p="sm" radius="sm" withBorder ta="center" style={{ borderTop: '3px solid var(--color-primary-900)' }}>
          <Text size="xs" c="dimmed">Assessment Records</Text>
          <Text fz={28} fw={700}>{stats.records}</Text>
        </Paper>
        <Paper p="sm" radius="sm" withBorder ta="center" style={{ borderTop: `3px solid ${primary[700]}` }}>
          <Text size="xs" c="dimmed">Geolocated</Text>
          <Text fz={28} fw={700} c={primary[700]}>{geoCount}</Text>
        </Paper>
        <Paper p="sm" radius="sm" withBorder ta="center" style={{ borderTop: '3px solid #d97706' }}>
          <Text size="xs" c="dimmed">Photos</Text>
          <Text fz={28} fw={700} c="#d97706">{totalPhotos}</Text>
        </Paper>
      </SimpleGrid>

      {/* Charts Row 1: Category Bar + Type Doughnut */}
      <SimpleGrid cols={{ base: 1, md: 2 }} mb="md">
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} size="sm" mb="sm">Damage Category</Text>
          <div style={{ height: 260 }}>
            <Bar data={categoryChartData} options={chartOptions} />
          </div>
        </Paper>
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} size="sm" mb="sm">Assessment Type</Text>
          <div style={{ height: 260 }}>
            <Doughnut data={typeChartData} options={doughnutOptions} />
          </div>
        </Paper>
      </SimpleGrid>

      {/* Timeline Chart */}
      {timelineChartData && (
        <Paper p="md" radius="sm" withBorder mb="md">
          <Text fw={600} size="sm" mb="sm">Assessment Timeline</Text>
          <div style={{ height: 280 }}>
            <Line data={timelineChartData} options={timelineOptions} />
          </div>
        </Paper>
      )}

      {/* Charts Row 2: Damage Type Horizontal Bar + Severity Bar */}
      <SimpleGrid cols={{ base: 1, md: 2 }} mb="md">
        {damageTypeEntries.length > 0 && (
          <Paper p="md" radius="sm" withBorder>
            <Text fw={600} size="sm" mb="sm">Damage Types</Text>
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              <div style={{ height: Math.max(200, damageTypeEntries.length * 28) }}>
                <Bar data={damageTypeChartData} options={horizontalBarOptions} />
              </div>
            </div>
          </Paper>
        )}
        {Object.keys(stats.bySeverity).length > 0 && (
          <Paper p="md" radius="sm" withBorder>
            <Text fw={600} size="sm" mb="sm">Severity Distribution</Text>
            <div style={{ height: 220 }}>
              <Bar data={severityChartData} options={chartOptions} />
            </div>
          </Paper>
        )}
      </SimpleGrid>

      {/* County Breakdown Table */}
      {countyData.length > 0 && (
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} size="sm" mb="sm">Assessments by County</Text>
          <div style={{ overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--frost)' }}>
                  <th style={{ padding: '8px 12px', textAlign: 'left' }}>County</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right' }}>Count</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right' }}>% of Total</th>
                </tr>
              </thead>
              <tbody>
                {countyData.map(([county, count]) => (
                  <tr key={county} style={{ borderBottom: '1px solid var(--frost)' }}>
                    <td style={{ padding: '8px 12px' }}>{county}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{count}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                      {total > 0 ? Math.round((count / total) * 100) : 0}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Paper>
      )}
    </div>
  );
}
